import { useRef, useEffect, useState, useContext } from "react";
import { useSelector } from "react-redux";
import classes from "./ServicesStudiosPage.module.css"
import { Link, useNavigate } from "react-router-dom";

import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import ProfileMenu from "../../components/NavBar/ProfileMenu";
import CreatorNavBar from "../../components/NavBar/CreatorNavBar/CreatorNavBar";
import StudioCard from "../../components/ServicesStudiosPage/StudioCard";
import ServiceCard from "../../components/ServicesStudiosPage/ServiceCard";
import { useUserStudiosPreview } from "../../hooks/useUser";
import ModesSwitch from "../../components/UI/ModesSwitch";
import { useViewportHeight } from '../../hooks/useViewportHeight';
import { NavBarContext } from '../../App';
import { useFromHash } from '../../hooks/useFromHash';
import Modal from "../../components/UI/Modal";
import { useCreativesByUserId } from "../../hooks/creative/useCreative.js";
import useUnreadMessagesCount from '../../hooks/messages/useUnreadMessagesCount';
import useBookingNotifications from '../../hooks/useBookingNotifications';
import useSearchParamsTabs from '../../hooks/useSearchParamsTabs';
import { trackProviderCreationIntent } from '../../util/tracking';
import { isHelpMenuPanelTarget } from '../../util/isHelpMenuPanelTarget';

/** Query values for ?bereich=… (order: Alle, Studios, Kreative Services). Example: /services-studios?bereich=studios */
export const SERVICES_STUDIOS_TAB_IDS = Object.freeze(['alle', 'studios', 'kreativ']);
const CREATIVE_SERVICE_HINT_STORAGE_KEY = 'servicesStudiosCreativeHintSeen_v1';

export const ServicesStudiosPage = () => {
    const TAB_SWITCH_DELAY_MS = 420;
    const { profilePicture, firstName, lastName, _id : userId } = useSelector((state) => state.auth.user);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const userContainerRef = useRef(null);
    const profileMenuRef = useRef(null);
    const navigate = useNavigate();
    const { setActiveView } = useContext(NavBarContext);
    const [showServiceErstellenModal, setShowServiceErstellenModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const { activeIndex: activeTab, setTabIndex } = useSearchParamsTabs({
        paramName: 'bereich',
        tabIds: SERVICES_STUDIOS_TAB_IDS,
        defaultIndex: 0,
    });
    const [visualTab, setVisualTab] = useState(activeTab);
    const [indicatorStyle, setIndicatorStyle] = useState({});
    // Separate "displayedTab" so we can animate the indicator first,
    // then swap the heavy tab content a moment later (smoother on mobile).
    const [displayedTab, setDisplayedTab] = useState(0);
    const listItemsRef = useRef([]);
    const navRef = useRef(null);
    const tabSwitchTimeoutRef = useRef(null);
    const mobileAddButtonWrapperRef = useRef(null);
    const desktopStudiosAddButtonWrapperRef = useRef(null);
    const desktopServicesAddButtonWrapperRef = useRef(null);
    const [showCreativeServiceHint, setShowCreativeServiceHint] = useState(false);

    const { creativesData: userCreativeStudiosData, loading: userCreativeStudiosLoading, error: userCreativeStudiosError } = useCreativesByUserId(userId);

    const options = [
      { title: "Studio oder Location", label2: "Du kannst einen kreativen Service jederzeit nachträglich hinzufügen.", value: "StudioErstellen" },
      { title: "Kreativer Service", label2: "Fotograf, Videograf oder Producer? Füge deinem Studio einen Service hinzu und werde gebucht.", value: "ServiceErstellen" },
    ];
    

    const { getInternalLinkProps } = useFromHash();

    const handleOptionChange = (value) => {
      setSelectedOption(value);
    };

    const handleWeiterClick = () => {
      if (!selectedOption) return;
      
      setShowServiceErstellenModal(false);
      trackProviderCreationIntent({
        creationType: selectedOption === "StudioErstellen" ? "studio" : "creative_service",
        source: "services_studios_modal"
      });
      
      if (selectedOption === "StudioErstellen") {
        const linkProps = getInternalLinkProps('/studio-erstellen');
        navigate(linkProps.to, { state: linkProps.state });
      } else if (selectedOption === "ServiceErstellen") {
        navigate('/services-erstellen');
      }
      
      // Reset selection after navigation
      setSelectedOption(null);
    };
    
    // Initialize viewport height fix for iOS Safari
    useViewportHeight();

    const { userData, loading: userLoading, error: userError } = useUserStudiosPreview(userId);
    const unreadCount = useUnreadMessagesCount();
    const { activeView } = useContext(NavBarContext);
    const bookingNotifications = useBookingNotifications(activeView);
    const hasNotifications = (unreadCount > 0) || (bookingNotifications > 0);

    const mappedStudios = userData?.studios
      ?.filter((studio) => studio && studio.status !== 'ARCHIVED' && studio.status !== 'OWNER_DELETED')
      .map(studio => ({
      id: studio?.id,
      name: studio?.StudioName,
      type: studio?.studioTypes?.join(', '),
      location: `${studio?.city}, ${studio?.country}`,
      image: studio?.processedCoverImage || '',
      status: studio?.status
    }));

    const mappedCreatives = userCreativeStudiosData?.creatives?.map(creative => ({
      id: creative?.id,
      displayName: creative?.displayName,
      jobTitle: creative?.jobTitle,
      description: creative?.description,
      profilePicture: creative?.processedProfilePicture || '',
      profilePictureKey: creative?.profilePicture || '',
      packageCount: creative?.packageCount || 0,
      projectCount: creative?.projectCount || 0,
      packages: creative?.packages?.items || [],
      projects: creative?.projects?.items || [],
      allProcessedProjectImages: creative?.allProcessedProjectImages || [],
      allProcessedProjectAudios: creative?.allProcessedProjectAudios || [],
      status: creative?.status,
      canBePublished: creative?.canBePublished ?? false,
      creativeType: creative?.creativeType,
      visualTypes: creative?.visualTypes,
      skills: creative?.skills || []
    })) || [];

    // Avoid showing loading spinners again once we already have cached data.
    const showStudiosLoader = userLoading && !userData?.studios;
    const showCreativesLoader = userCreativeStudiosLoading && !userCreativeStudiosData?.creatives;

    useEffect(() => {
      if (userLoading) return;
      if (mappedStudios?.length === 0) {
        setActiveView('explorer');
        navigate('/');
      }
    }, [mappedStudios, userLoading, navigate, setActiveView]);

    useEffect(() => {
        const activeItem = listItemsRef.current[visualTab];
        const navElement = navRef.current;
        
        if (activeItem && navElement) {
            const navRect = navElement.getBoundingClientRect();
            const itemRect = activeItem.getBoundingClientRect();
            
            setIndicatorStyle({
                left: `${itemRect.left - navRect.left}px`,
                width: `${itemRect.width}px`
            });
        }
    }, [visualTab]);

    useEffect(() => {
      setVisualTab(activeTab);
      setDisplayedTab(activeTab);
    }, [activeTab]);

    useEffect(() => {
      return () => {
        if (tabSwitchTimeoutRef.current) {
          window.clearTimeout(tabSwitchTimeoutRef.current);
        }
      };
    }, []);

    const handleTabSwitch = (targetTab) => {
      setVisualTab(targetTab);

      if (tabSwitchTimeoutRef.current) {
        window.clearTimeout(tabSwitchTimeoutRef.current);
      }

      tabSwitchTimeoutRef.current = window.setTimeout(() => {
        setTabIndex(targetTab);
        tabSwitchTimeoutRef.current = null;
      }, TAB_SWITCH_DELAY_MS);
    };

    const handleProfileMenuClick = () => {
        setShowProfileMenu(prev => !prev);
    };

    const handleStudioCardClick = (studioId) => {
        navigate(`/services-studios/editor/${studioId}`);
    };

    const handleMessagesClick = () => {
        setActiveView('creator');
        navigate('/messages');
    };

    const dismissCreativeServiceHint = () => {
      setShowCreativeServiceHint(false);
      localStorage.setItem(CREATIVE_SERVICE_HINT_STORAGE_KEY, 'true');
    };

    const getVisibleDesktopHintTarget = () => {
      if (desktopStudiosAddButtonWrapperRef.current) {
        return desktopStudiosAddButtonWrapperRef.current;
      }
      if (desktopServicesAddButtonWrapperRef.current) {
        return desktopServicesAddButtonWrapperRef.current;
      }
      return null;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
          if (isHelpMenuPanelTarget(event.target)) {
            return;
          }

          if (
            profileMenuRef.current && 
            !profileMenuRef.current.contains(event.target) &&
            !userContainerRef.current?.contains(event.target)
          ) {
            setShowProfileMenu(false);
          }
        };
    
        document.addEventListener('mousedown', handleClickOutside);
    
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, []);  

    useEffect(() => {
      const hintAlreadySeen = localStorage.getItem(CREATIVE_SERVICE_HINT_STORAGE_KEY) === 'true';
      if (!hintAlreadySeen) {
        setShowCreativeServiceHint(true);
      }
    }, []);

    useEffect(() => {
      if (!showCreativeServiceHint) {
        return;
      }

      const handleHintOutsideClick = (event) => {
        const clickedInsideMobileHint = mobileAddButtonWrapperRef.current?.contains(event.target);
        const clickedInsideDesktopStudiosHint = desktopStudiosAddButtonWrapperRef.current?.contains(event.target);
        const clickedInsideDesktopServicesHint = desktopServicesAddButtonWrapperRef.current?.contains(event.target);

        if (clickedInsideMobileHint || clickedInsideDesktopStudiosHint || clickedInsideDesktopServicesHint) {
          return;
        }

        dismissCreativeServiceHint();
      };

      document.addEventListener('mousedown', handleHintOutsideClick);
      document.addEventListener('touchstart', handleHintOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleHintOutsideClick);
        document.removeEventListener('touchstart', handleHintOutsideClick);
      };
    }, [showCreativeServiceHint]);

    useEffect(() => {
      if (!showCreativeServiceHint) {
        return;
      }

      if (window.innerWidth <= 642) {
        return;
      }

      const scrollToHint = () => {
        const target = getVisibleDesktopHintTarget();
        if (!target) {
          return;
        }

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      const timeoutId = window.setTimeout(scrollToHint, 120);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [showCreativeServiceHint, activeTab, mappedStudios?.length, mappedCreatives.length]);

  return (

    <div className={classes.servicesStudiosPage}>
        {showCreativeServiceHint && (
          <button
            type="button"
            className={classes.creativeServiceHintOverlay}
            aria-label="Hinweis schließen"
            onClick={dismissCreativeServiceHint}
          />
        )}
        <header className={classes.header}>
            <Link to="/" className={classes.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
                    <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
                    <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
                    <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
                </svg>
            </Link>
            <Link to="/" className={classes.navigationLinks}>
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                    <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                    <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                    <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
            </Link>
            <CreatorNavBar 
                onMessagesClick={handleMessagesClick}
            />
            <div className={classes.headerContent}>
              <ModesSwitch />
              <div className={classes.userWrapper}>
                  {hasNotifications && (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="13" 
                      height="13" 
                      viewBox="0 0 13 13" 
                      fill="none"
                      style={{
                        position: 'absolute',
                        top: '0',
                        right: '0',
                        zIndex: 10
                      }}
                    >
                      <path 
                        d="M6.5 0.75C9.67573 0.75 12.25 3.32438 12.25 6.5C12.25 9.6756 9.67571 12.25 6.5 12.25C3.32448 12.25 0.750019 9.67561 0.75 6.5C0.75 3.32436 3.32446 0.75 6.5 0.75Z" 
                        fill="var(--Stu-Rot, #DF315A)" 
                        stroke="var(--Black, #000)" 
                        strokeWidth="1.5"
                      />
                    </svg>
                  )}
                  <div 
                      ref={userContainerRef} 
                      onClick={handleProfileMenuClick} 
                      className={classes.userContainer}
                      style={{ position: 'relative' }}
                    >
                      <div className={classes.userImageContainer}>
                        <img src={profilePicture} alt="User Profile" /> 
                      </div>
                  </div>
              </div>
            </div>
        </header>
        <div className={classes.contentContainer}>
            <div className={classes.contentHeader}>
                <h3 className={`${classes.pageTitle} titleSemibold2`}>Deine Studios & Services</h3>
                <div ref={mobileAddButtonWrapperRef} className={classes.mobileAddButtonWrapper}>
                  <button className={classes.addNewStudioButton} onClick={() => setShowServiceErstellenModal(true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 8.73873H1V7.66242H17V8.73873Z" fill="#1F1F1F"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.207031V16.207H8.45801V0.207031H9.53433Z" fill="#1F1F1F"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 8.73873H1V7.66242H17V8.73873Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.207031V16.207H8.45801V0.207031H9.53433Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                      </svg>
                  </button>
                  {showCreativeServiceHint && (
                    <div className={classes.mobileCreativeServiceHint}>
                      <h4 className="titleSemibold3">Neu: Kreativ-Service anbieten</h4>
                      <p className="regularSmall">
                        Erstelle einen Kreativ-Service, verbinde es mit deinem Studio und werde direkt mitgebucht.
                      </p>
                    </div>
                  )}
                </div>
            </div>
            <div className={classes.navigationContainer}>
                <nav ref={navRef} className={classes.navigationList}>
                    <li 
                        ref={el => listItemsRef.current[0] = el}
                        className={`semibold large ${visualTab === 0 ? classes.active : classes.inactive}`}
                        onClick={() => handleTabSwitch(0)}
                    >
                        Alle
                    </li>
                    <li 
                        ref={el => listItemsRef.current[1] = el}
                        className={`semibold large ${visualTab === 1 ? classes.active : classes.inactive}`}
                        onClick={() => handleTabSwitch(1)}
                    >
                        Studios
                    </li>
                    <li 
                        ref={el => listItemsRef.current[2] = el}
                        className={`semibold large ${visualTab === 2 ? classes.active : classes.inactive}`}
                        onClick={() => handleTabSwitch(2)}
                    >
                        Kreative Services
                    </li>
                </nav>

                <div className={classes.indicatorContainer}>
                    <div 
                        className={classes.indicator} 
                        style={indicatorStyle}
                    />
                    <div className={classes.horizontalLine}></div>
                </div>
            </div>
            <div className={classes.studiosAndServicesContainer}>
            <div
              className={`${classes.studiosContainer} ${
                (displayedTab === 0 || displayedTab === 1) ? '' : classes.hiddenTab
              }`}
            >
                {showStudiosLoader ? (
                    <div className={classes.loadingContainer}>
                        <div className={classes.loadingSpinner}></div>
                    </div>
                ) : mappedStudios?.length > 0 ? (
                    mappedStudios.map((studio) => (
                        <StudioCard  
                            key={studio.id} 
                            studio={studio} 
                            fromServicesStudios={true}
                            onClick={() => handleStudioCardClick(studio.id)}
                        />
                    ))
                ) : (
                    <div className={classes.noStudiosContainerPhone}>
                        <div className={classes.defaultStudioContainer}>
                          <div className={classes.opacityOverlay}></div>
                          <div className={classes.defaultStudioImage}></div>
                          <div className={classes.defaultStudioText}>
                            <div className={classes.defaultStudioTextRight}>
                              <div className={classes.defaultStudioTextRightTop}></div>
                              <div className={classes.defaultStudioTextRightBottom}></div>
                            </div>
                            <div className={classes.defaultStudioTextLeft}>
                              <div className={classes.defaultStudioLeftTop}></div>
                            </div>
                          </div>
                        </div>
                        <div className={classes.defaultStudioContainer}>
                          <div className={classes.opacityOverlay2}></div>
                          <div className={classes.defaultStudioImage}></div>
                          <div className={classes.defaultStudioText}>
                            <div className={classes.defaultStudioTextRight}>
                              <div className={classes.defaultStudioTextRightTop}></div>
                              <div className={classes.defaultStudioTextRightBottom}></div>
                            </div>
                            <div className={classes.defaultStudioTextLeft}>
                              <div className={classes.defaultStudioLeftTop}></div>
                            </div>
                          </div>
                        </div>
                        <div className={classes.noStudiosTextContainer}>
                          <h2 className="titleSemibold">Erstelle dein Studio</h2>
                          <p className="mediumLarge">In wenigen Klicks Studio erstellen und gebucht werden</p>
                          <button className={classes.createStudioButton}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                              <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" fill="white"/>
                              <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" fill="white"/>
                              <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                              <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                            </svg>
                            Studio erstellen
                          </button>
                        </div>
                    </div>
                )}
                {displayedTab === 1 && (
                    <div style={{ overflow: showCreativeServiceHint ? 'visible' : 'hidden' }} className={classes.addStudioContainer} onClick={() => setShowServiceErstellenModal(true)}>
                      <div style={{ overflow: showCreativeServiceHint ? 'visible' : 'hidden' }} ref={desktopStudiosAddButtonWrapperRef} className={classes.desktopAddButtonWrapper}>
                        <div className={classes.addStudioButton}>
                            <span>Neues Studio oder Service</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                              <path fillRule="evenodd" clipRule="evenodd" d="M17 8.73873H1V7.66242H17V8.73873Z" fill="#1F1F1F"/>
                              <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.207031V16.207H8.45801V0.207031H9.53433Z" fill="#1F1F1F"/>
                              <path fillRule="evenodd" clipRule="evenodd" d="M17 8.73873H1V7.66242H17V8.73873Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                              <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.207031V16.207H8.45801V0.207031H9.53433Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                            </svg>
                        </div>
                        {showCreativeServiceHint && (
                          <div className={classes.desktopCreativeServiceHint}>
                            <h4 className="titleSemibold3">Neu: Kreativ-Service anbieten</h4>
                            <p className="regularSmall">
                              Erstelle einen Kreativ-Service, verbinde es mit deinem Studio und werde direkt mitgebucht.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                )}
            </div>
                <div
                  className={`${classes.servicesContainer} ${
                    (displayedTab === 0 || displayedTab === 2) ? '' : classes.hiddenTab
                  }`}
                >
                    {showCreativesLoader ? (
                        <div className={classes.loadingContainer}>
                            <div className={classes.loadingSpinner}></div>
                        </div>
                    ) : mappedCreatives.length > 0 ? (
                        mappedCreatives.map((creative) => (
                            <ServiceCard  
                                key={creative.id} 
                                creative={creative}
                            />
                        ))
                    ) : null}
                    {(displayedTab === 0 || displayedTab === 2) && (
                        <div style={{ overflow: showCreativeServiceHint ? 'visible' : 'hidden' }} className={classes.addStudioContainer} onClick={() => setShowServiceErstellenModal(true)}>
                          <div style={{ overflow: showCreativeServiceHint ? 'visible' : 'hidden' }} ref={desktopServicesAddButtonWrapperRef} className={classes.desktopAddButtonWrapper}>
                            <div className={classes.addStudioButton}>
                                <span>Neues Studio oder Service</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                                  <path fillRule="evenodd" clipRule="evenodd" d="M17 8.73873H1V7.66242H17V8.73873Z" fill="#1F1F1F"/>
                                  <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.207031V16.207H8.45801V0.207031H9.53433Z" fill="#1F1F1F"/>
                                  <path fillRule="evenodd" clipRule="evenodd" d="M17 8.73873H1V7.66242H17V8.73873Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                                  <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.207031V16.207H8.45801V0.207031H9.53433Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                                </svg>
                            </div>
                            {showCreativeServiceHint && (
                              <div className={classes.desktopCreativeServiceHint}>
                                <h4 className="titleSemibold3">Neu: Kreativ-Service anbieten</h4>
                                <p className="regularSmall">
                                  Erstelle einen Kreativ-Service, verbinde es mit deinem Studio und werde direkt mitgebucht.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                    )}
                </div>
            </div>
            </div>
        <div className={classes.mobileNavBar}>
            <NavBarPhone fromServicesStudios={true} />
        </div>
        {showProfileMenu && (
        <ProfileMenu
          ref={profileMenuRef}
          profilePicture={profilePicture}
          firstName={firstName}
          lastName={lastName}
          setShowProfileMenu={setShowProfileMenu}
          //fromProfile={fromProfile}
          />
        )}
        <Modal className={"serviceErstellenModal"} open={showServiceErstellenModal} onBackdropClick={() => {
          setShowServiceErstellenModal(false);
          setSelectedOption(null);
        }}>
          <div className={classes.serviceErstellenModalContent}>
            <header className={classes.serviceErstellenModalHeader}>
              <div className={classes.closeButton} onClick={() => setShowServiceErstellenModal(false)}>
                <svg className={classes.serviceErstellenModalCloseButton} onClick={() => setShowServiceErstellenModal(false)} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
              </div>
              <h2 className="titleSemibold2">Was möchtest du anbieten?</h2>
              <p className="slimRegularLargeText" style={{color: "var(--dark-grey-3)"}}>Du kannst kreative Services aktuell nur zusammen mit einem Studio anbieten.</p>
            </header>
            <div className={classes.serviceErstellenModalOptions}>
            {options.map((option) => (
                <div 
                    key={option.value} 
                    className={classes.option}
                    onClick={() => handleOptionChange(option.value)}
                >
                    <div 
                        className={classes.containerLabels}
                    >
                        <label style={{marginBottom: "6px"}} className="titleSemibold3">{option.title}</label>
                        <label className={`${classes.label} slimRegularSmallText`}>{option.label2}</label>
                    </div>
                    <input
                    className={classes.inputs}
                    type="checkbox"
                    id={option.value}
                    name="studioType"
                    value={option.value}
                    checked={selectedOption === option.value}
                    onChange={() => handleOptionChange(option.value)}    
                    />
                </div>
              ))}
            </div>
            <div className={classes.serviceErstellenModalButtonContainer}>
              <button 
                className={`${classes.serviceErstellenModalButton} semiboldSmall`}
                onClick={handleWeiterClick}
                disabled={!selectedOption}
              >
                Weiter
              </button>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default ServicesStudiosPage;
